import type { FormEventHandler } from 'react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextInput from 'components/Common/Form/Input/TextInput'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { registerWithOTP, sendSignUpOTPCode, verifySignUpOTPCode } from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import * as Analytics from 'analytics/analytics'
import HelpPane from 'components/Common/HelpPane'
import ContactContext from 'contexts/contactContext'
import TextLink from 'LuxKit/TextLink'
import useInterval from 'hooks/useInterval'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { PendingSignUpOTPData } from './AccountAccessModalContent'
import { formatPhoneNumber, sanitisePhoneNumber } from 'lib/format/formatPhoneNumber'

interface OTPForm {
  code: string
}

interface Props {
  pendingSignUpOTPData: PendingSignUpOTPData
  onModeChange?: (mode: App.UiAccountModalMode) => void
  hasDismissed?: boolean
}

function AccountAccessSignUpOTP(props: Props) {
  const {
    pendingSignUpOTPData,
    onModeChange = noop,
    hasDismissed,
  } = props

  const intl = useIntl()
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const reRef = useRef<ReCAPTCHA>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const dispatch = useAppDispatch()
  const error = useAppSelector(state => state.auth.error)
  const processing = useAppSelector(state => state.auth.processing)
  const signUpOTPVerified = useAppSelector(state => state.auth.signUpOTPVerified)
  const [countDown, setCountDown] = useState<number>(30)
  const [isVerifying, setIsVerifying] = useState(false)

  useInterval(
    () => { setCountDown((curr) => Math.max(curr - 1, 0)) },
    1000,
    { disabled: countDown <= 0 },
  )

  const currentContact = useContext(ContactContext)
  const contactNumber = currentContact.defaultContact

  const formattedPhone = formatPhoneNumber(sanitisePhoneNumber(pendingSignUpOTPData.phone ?? ''), pendingSignUpOTPData.phonePrefix ?? '')

  // When OTP is verified, proceed with registration
  useEffect(() => {
    if (signUpOTPVerified && isVerifying) {
      setIsVerifying(false)
      dispatch(registerWithOTP({
        email: pendingSignUpOTPData.email,
        password: pendingSignUpOTPData.password,
        givenName: pendingSignUpOTPData.givenName,
        surname: pendingSignUpOTPData.surname,
        phone: pendingSignUpOTPData.phone,
        phonePrefix: pendingSignUpOTPData.phonePrefix,
        postcode: pendingSignUpOTPData.postcode,
      }))
    }
  }, [signUpOTPVerified, isVerifying, dispatch, pendingSignUpOTPData])

  // When logged in (registration successful), resolve the modal
  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  // Reset verifying state on error
  useEffect(() => {
    if (error && isVerifying) {
      setIsVerifying(false)
    }
  }, [error, isVerifying])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'sign_up_otp',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [])

  const phoneNumber: App.PhoneNumber = useMemo(() => ({
    phone: pendingSignUpOTPData.phone as string,
    prefix: pendingSignUpOTPData.phonePrefix as string,
  }), [pendingSignUpOTPData.phone, pendingSignUpOTPData.phonePrefix])

  const onResendCode = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'sign_up_otp_resend',
      action: 'interaction',
      category: 'logging',
      type: 'operational',
    })
    setCountDown(30)
    dispatch(sendSignUpOTPCode(phoneNumber))
  }, [dispatch, phoneNumber])

  const onValidateCode = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    setIsVerifying(true)

    const formData = formToObject<OTPForm>(event.currentTarget)
    dispatch(verifySignUpOTPCode(phoneNumber, formData.code))
  }, [dispatch, phoneNumber])

  const onBack = useCallback(() => onModeChange('join'), [onModeChange])

  const isSubmitting = processing || isVerifying

  const areYouSureTitle = intl.formatMessage({
    id: 'obukDow62p',
    defaultMessage: 'Are you sure?',
    description: 'Modal title when user tries to dismiss the sign-up OTP verification',
  })

  const verificationCodeTitle = intl.formatMessage({
    id: 'wpsViaQU1v',
    defaultMessage: 'Verification code',
    description: 'Modal title for sign-up OTP verification step',
  })

  // warningText uses FormattedMessage inline (rich text with tel: link)

  const serverErrorText = intl.formatMessage({
    id: 'CokbDsgDRw',
    defaultMessage: 'Something went wrong. Please try again later.',
    description: 'Generic server error message during sign-up OTP verification',
  })

  // instructionsText uses FormattedMessage inline (rich text with <strong>)

  const codeLabel = intl.formatMessage({
    id: 'H/Ftw8iHcu',
    defaultMessage: '6-digit code',
    description: 'Label for OTP code input field during sign-up',
  })

  const codePlaceholder = intl.formatMessage({
    id: '8mk6zcOukQ',
    defaultMessage: 'Enter 6-digit code',
    description: 'Placeholder for OTP code input during sign-up',
  })

  const codeRequiredError = intl.formatMessage({
    id: 'mXmn0i3aOy',
    defaultMessage: 'Code is required',
    description: 'Validation error when OTP code field is empty during sign-up',
  })

  const codeInvalidError = intl.formatMessage({
    id: '/bpgjmEo7i',
    defaultMessage: 'Your code must be 6 digits long',
    description: 'Validation error when OTP code is not 6 digits during sign-up',
  })

  const submitLabel = intl.formatMessage({
    id: 'DWnCJGSLzf',
    defaultMessage: 'Submit',
    description: 'Submit button text for OTP verification during sign-up',
  })

  const didntReceiveText = intl.formatMessage({
    id: '5qw5gXcFCN',
    defaultMessage: 'Didn\u2019t receive the SMS?',
    description: 'Help text asking if user received the SMS during sign-up OTP',
  })

  const resendCountdownLabel = intl.formatMessage({
    id: '3dy7JX3aJ3',
    defaultMessage: 'Resend code in {countDown}s',
    description: 'Countdown text before user can resend OTP code during sign-up',
  }, { countDown })

  const resendLabel = intl.formatMessage({
    id: '2htZv6BsPm',
    defaultMessage: 'Resend code',
    description: 'Link text to resend OTP code during sign-up',
  })

  return (<>
    <ModalHeader
      title={hasDismissed ? areYouSureTitle : verificationCodeTitle}
      onBackButtonClick={onBack}
      dismissible={false}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24} as="form" onSubmit={onValidateCode} name="signUpOTPForm">
          {hasDismissed && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
            <FormattedMessage
              id="pPfdmIXOD7"
              defaultMessage="You must verify your phone number to create an account. Alternatively, call our 24/7 support team on <link>{contactNumber}</link>."
              description="Warning text when user tries to skip phone verification during sign-up"
              values={{
                contactNumber: contactNumber.humanReadable,
                link: (chunks) => <TextLink href={`tel:${contactNumber.number}`} rel="nofollow">{chunks}</TextLink>,
              }}
            />
          </MessageBanner>}
          {error?.status === 500 && <MessageBanner kind="critical">{serverErrorText}</MessageBanner>}
          {error?.message && error?.status !== 500 && <MessageBanner kind="critical">{error.message}</MessageBanner>}
          <BodyText variant="medium">
            <FormattedMessage
              id="yV+vkLCKjc"
              defaultMessage="We’ve sent a 6-digit code to <strong>{phone}</strong>. The code is valid for 10 minutes and after that you will need to request a new one."
              description="Instructions text explaining OTP code was sent during sign-up"
              values={{
                phone: formattedPhone,
                strong: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </BodyText>
          <TextInput
            type="text"
            required
            id="code"
            name="code"
            label={codeLabel}
            placeholder={codePlaceholder}
            requiredErrorMessage={codeRequiredError}
            minLength={6}
            maxLength={6}
            pattern="^\d*$"
            invalidErrorMessage={codeInvalidError}
            inputMode="numeric"
          />
          {(!error || (!!error && error.status !== 500)) && (
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <TextButton
                kind="primary"
                size="large"
                type="submit"
                fit="flex"
                disabled={isSubmitting}
              >
                {submitLabel}
              </TextButton>
              <Group direction="horizontal" gap={8}>
                <BodyText variant="medium">{didntReceiveText}</BodyText>
                <TextLink size="medium" onClick={onResendCode} disabled={countDown > 0}>
                  {countDown > 0 ? resendCountdownLabel : resendLabel}
                </TextLink>
              </Group>
            </Group>
          )}
          {error?.status === 500 && (
            <HelpPane contactNumber={contactNumber} />
          )}
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
  )
}

export default AccountAccessSignUpOTP
